/**
 * ExpiredIdCardOverride — amber banner + opt-in override for expired ID cards.
 *
 * Story 70-1E-a (E-70 OCR). Used by rental wizard PartnerStep + worksheet
 * wizard PartnerStep. Backend DTO shape: `overrideExpiredId?: boolean` +
 * `overrideReason?: string` (commit cd4a685c). The reason is MANDATORY when
 * the checkbox is checked — backend rejects with BadRequest otherwise.
 *
 * Contract:
 *   - `idCardExpiry` is the partner's stored expiry (ISO date string). If
 *     `null/undefined` OR the date is not in the past, this component renders
 *     NOTHING — fully silent.
 *   - Emits `onChange({ override, reason })` whenever the state changes, so
 *     the parent can ship the values in its POST payload.
 */

import { useTranslation } from '@kgc/i18n';
import { useEffect, useState } from 'react';

export interface ExpiredIdCardOverrideState {
  override: boolean;
  reason: string;
  /** Convenience: true when override is on but reason is empty/whitespace. */
  missingReason: boolean;
}

export interface ExpiredIdCardOverrideProps {
  /** ISO date string of partner.idCardExpiry (may be undefined/null). */
  idCardExpiry?: string | null | undefined;
  onChange: (state: ExpiredIdCardOverrideState) => void;
  /** Optional clock injection for tests. */
  now?: Date;
  /** Forwarded data-testid on the root container. */
  'data-testid'?: string;
}

/**
 * Days since the expiry. Negative if still valid (caller uses this to decide
 * whether to render at all — we only show the banner when strictly past).
 */
function daysSinceExpiry(expiryIso: string, now: Date): number {
  const expiry = new Date(expiryIso);
  if (Number.isNaN(expiry.getTime())) return -1;
  const MS_PER_DAY = 86_400_000;
  return Math.floor((now.getTime() - expiry.getTime()) / MS_PER_DAY);
}

export function ExpiredIdCardOverride({
  idCardExpiry,
  onChange,
  now,
  'data-testid': dataTestId,
}: ExpiredIdCardOverrideProps) {
  const { t } = useTranslation();
  const [override, setOverride] = useState(false);
  const [reason, setReason] = useState('');

  // Clock is frozen per-render (no live recompute) — the banner is driven by
  // partner selection, not by the wall clock ticking past expiry mid-session.
  const clock = now ?? new Date();
  const isPast =
    typeof idCardExpiry === 'string' &&
    idCardExpiry.length > 0 &&
    daysSinceExpiry(idCardExpiry, clock) > 0;

  // Reset state when the partner (i.e. idCardExpiry) changes.
  useEffect(() => {
    setOverride(false);
    setReason('');
  }, [idCardExpiry]);

  // Notify parent on every change (including the implicit reset above).
  useEffect(() => {
    const missingReason = override && reason.trim().length === 0;
    onChange({ override, reason: reason.trim(), missingReason });
    // `onChange` intentionally excluded — parent typically wraps in useCallback
    // but we don't want to re-fire when only its identity flips.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [override, reason]);

  if (!isPast || !idCardExpiry) return null;

  const days = daysSinceExpiry(idCardExpiry, clock);
  const missingReason = override && reason.trim().length === 0;

  return (
    <div
      role="alert"
      className="rounded-none border-2 p-3 text-sm"
      style={{
        borderColor: '#F59E0B',
        backgroundColor: 'rgba(245, 158, 11, 0.08)',
      }}
      data-testid={dataTestId ?? 'expired-id-override'}
    >
      <p className="font-medium text-amber-900 dark:text-amber-300">
        {t('partner.expired.banner', { days })}
      </p>
      <label className="mt-2 flex cursor-pointer items-center gap-2 text-amber-900 dark:text-amber-200">
        <input
          type="checkbox"
          checked={override}
          onChange={e => setOverride(e.target.checked)}
          data-testid="expired-id-override-checkbox"
        />
        <span>{t('partner.expired.overrideCheckbox')}</span>
      </label>
      {override && (
        <div className="mt-2 space-y-1">
          <label className="block text-xs font-medium text-amber-900 dark:text-amber-200">
            {t('partner.expired.overrideReasonLabel')}
          </label>
          <textarea
            value={reason}
            onChange={e => setReason(e.target.value)}
            placeholder={t('partner.expired.overrideReasonPlaceholder')}
            rows={2}
            className="w-full rounded-none border-2 border-amber-300 bg-white p-2 text-sm dark:border-amber-700 dark:bg-gray-900"
            data-testid="expired-id-override-reason"
          />
          {missingReason && (
            <p className="text-xs text-red-600 dark:text-red-400">
              {t('partner.expired.overrideReasonRequired')}
            </p>
          )}
        </div>
      )}
    </div>
  );
}
